import { ORDER_STATUSES } from "@/api/order/_models/Order";
import type { OrderStatus } from "@/api/order/_models/Order";
import { ORDER_LIST_EMPTY_FILTER } from "./OrderListFilterPanel";
import type { OrderListFilterValues } from "./OrderListFilterPanel";

const SEARCH = "search";
const SOURCE = "source";
const STATUS = "status";
const CUSTOMER_ID = "customerId";
const ORDER_DATE_FROM = "orderDateFrom";
const ORDER_DATE_TO = "orderDateTo";

const FILTER_KEYS = [SEARCH, SOURCE, STATUS, CUSTOMER_ID, ORDER_DATE_FROM, ORDER_DATE_TO];

/** The filter panel's open state in the URL: ?filter=open. */
export const ORDER_LIST_FILTER_PANEL_PARAM = "filter";
const FILTER_PANEL_OPEN = "open";

const DAY = /^\d{4}-\d{2}-\d{2}$/;

const isOrderStatus = (value: string): value is OrderStatus => {
  return ORDER_STATUSES.some((status) => status === value);
};

const toDay = (value: string | null): string | null => {
  if (value && DAY.test(value)) {
    return value;
  }
  return null;
};

/**
 * The order list's filters from the URL (e.g. ?status=Shipped&status=Completed&source=CIN7&orderDateFrom=2026-10-01).
 * Invalid values (an unknown status, a malformed date) are dropped.
 */
export const toOrderListFilterFromSearchParams = (params: URLSearchParams): OrderListFilterValues => {
  return {
    ...ORDER_LIST_EMPTY_FILTER,
    search: params.get(SEARCH) ?? "",
    sources: params.getAll(SOURCE),
    statuses: params.getAll(STATUS).filter((status) => isOrderStatus(status)),
    customerIds: params.getAll(CUSTOMER_ID),
    orderDateFrom: toDay(params.get(ORDER_DATE_FROM)),
    orderDateTo: toDay(params.get(ORDER_DATE_TO)),
  };
};

/** Whether the URL says the filter panel is open. */
export const isOrderListFilterPanelOpenInSearchParams = (params: URLSearchParams): boolean => {
  return params.get(ORDER_LIST_FILTER_PANEL_PARAM) === FILTER_PANEL_OPEN;
};

/**
 * Writes the filter and the panel state into `params`, replacing what was there and leaving
 * every other param alone. Lists repeat the key; empty values are left out.
 */
export const writeOrderListFilterToSearchParams = (
  params: URLSearchParams,
  filter: OrderListFilterValues,
  isPanelOpen: boolean,
): void => {
  for (const key of [...FILTER_KEYS, ORDER_LIST_FILTER_PANEL_PARAM]) {
    params.delete(key);
  }
  if (isPanelOpen) params.set(ORDER_LIST_FILTER_PANEL_PARAM, FILTER_PANEL_OPEN);
  const search = filter.search.trim();
  if (search) params.set(SEARCH, search);
  for (const source of filter.sources) params.append(SOURCE, source);
  for (const status of filter.statuses) params.append(STATUS, status);
  for (const customerId of filter.customerIds) params.append(CUSTOMER_ID, customerId);
  if (filter.orderDateFrom) params.set(ORDER_DATE_FROM, filter.orderDateFrom);
  if (filter.orderDateTo) params.set(ORDER_DATE_TO, filter.orderDateTo);
};
